// R13 — THE LIBRARY SEARCH HIT, and the reason it left App.tsx is the reason on this whole roadmap
// line: nothing had ever CALLED it.
//
// What `SnippetText` does with the server's markers was held by regexes over a 14,000-line file — a
// `<mark className="book-hit__mark">` presence match that is true whether or not the parser ever
// emits a hit part, and `!dangerouslySetInnerHTML`, a NEGATIVE regex, which passes by finding nothing
// (R8-4). Out here both components take props, hold no state and call no hook, so
// `scripts/r13-search-hit.mjs` CALLS them and reads the tree back: every marked span really is a
// <mark>, every unmarked character really survives, and no sentinel character reaches a rendered
// string.
//
// The marker law itself is NOT here — it is `src/searchHits.js`, shared with the server, because the
// half that writes the markers and the half that reads them are one law.
import { splitSnippet } from "../searchHits.js";
// 14-B7d(2): a DEFAULT language, because `r13-search-hit.mjs` CALLS this component with no language
// and reads its English back. What the SERVER matched is untouched — `SnippetText` renders the
// member's own book text and takes no language at all.
import { t } from "../strings.js";
import { DEFAULT_LANGUAGE } from "../language.js";
// R93: the state shape and its failure sentence live with the transport that produces them.
import { searchFailureNote, type BookSearchState } from "../lib/library-search";

// A hit is what `shapeHit` in server/db/library.js hands back, one per matched page.
export type BookHit = { slug: string; title: string; author: string; page: number; snippet: string };

// The server wraps what it matched in sentinel characters rather than HTML (see src/searchHits.js),
// so highlighting is a string split and the DOM only ever receives text. No dangerouslySetInnerHTML
// anywhere near a book's contents.
export function SnippetText({ snippet }: { snippet: string }) {
  return (
    <>
      {splitSnippet(snippet).map((p, i) => (
        p.hit ? <mark key={i} className="book-hit__mark">{p.text}</mark> : <span key={i}>{p.text}</span>
      ))}
    </>
  );
}

// SIX STATES, AND EACH ONE OWES THE MEMBER A DIFFERENT SENTENCE. A search box that renders "nothing
// found" while it is still searching, or renders nothing at all when she is signed out, teaches her
// the library has no text in it. The order of these guards is the law: too-short and loading are
// decided before `ran`, because a member typing her second query is both "has run before" and
// "searching now".
export function BookHitList({ state, term, onOpen, showTitles, language = DEFAULT_LANGUAGE }: {
  state: BookSearchState;
  term: string;
  onOpen: (hit: BookHit) => void;
  showTitles: boolean;
  language?: string;
}) {
  if (term.trim().length < 3) return null;
  if (state.loading) return <p className="book-hits__note">{t(language, "hits.searching")}</p>;
  if (state.needsAuth) return <p className="book-hits__note">{t(language, "hits.signIn")}</p>;
  if (!state.ran) return null;
  // R93 — **THE SIXTH STATE, AND IT IS THE ONE THIS HEADER PROMISED AND THE HOOK COULD NOT PRODUCE.**
  // A search that FAILED and a search that matched nothing arrived here as the same state, so a
  // dropped request, a 500 carrying the server's own sentence, and a shelf with no stored text all
  // drew "Nothing inside the pages for X" — a claim about 28.8M characters of book text, in her own
  // language, on a search that never ran. It is decided BEFORE the empty case by POSITION (R52), so
  // no later spelling can put the wrong sentence back in front of it.
  const failure = searchFailureNote(state.failed, {
    failed: t(language, "hits.failed"),
    unavailable: t(language, "hits.unavailable"),
  });
  if (failure) return <p className="book-hits__note">{failure}</p>;
  if (!state.hits.length) {
    return <p className="book-hits__note">{t(language, "hits.none", { term: term.trim() })}</p>;
  }
  // TWO AXES, FOUR ROWS, AND NEITHER IS A `{s}` TOKEN: Spanish agrees the article with the noun, and
  // "inside the book" / "inside the books" is a second fact about WHICH search this was.
  // **THE FOUR KEYS ARE LITERALS AND THAT IS 14-B2's LESSON, NOT A STYLE CHOICE:** an assembled key
  // is a key no grep, no gate and nobody renaming a row can find, so the catalog's own "is this row
  // read by anything" law goes blind to all four at once.
  const countKey = state.hits.length === 1
    ? (showTitles ? "hits.oneInBooks" : "hits.oneInBook")
    : (showTitles ? "hits.manyInBooks" : "hits.manyInBook");
  return (
    <div className="book-hits">
      <p className="book-hits__note">{t(language, countKey, { count: state.hits.length })}</p>
      <ul className="book-hits__list">
        {state.hits.map((hit) => (
          <li key={`${hit.slug}-${hit.page}`}>
            <button type="button" className="book-hit" onClick={() => onOpen(hit)}>
              {showTitles ? <span className="book-hit__title">{hit.title}</span> : null}
              <span className="book-hit__page">{t(language, "hits.page", { page: hit.page })}</span>
              <span className="book-hit__snippet"><SnippetText snippet={hit.snippet} /></span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
